import { Card, Switch } from '@radix-ui/themes'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Globe, X } from 'lucide-react'
import SearchBar from '@/components/SearchBar'
import storageService from '@/services/wallet/storage.service'
import { getPopularTokens } from '@/config/tokens'
import { getNetwork } from '@/config/networks'
import { getAssetNetworkIds } from '@/hooks/useBalance'
import { useBalance } from '@/hooks/useBalance'
import { useWalletStore } from '@/store/walletStore'

const NATIVE_ADDRESS = '0x0000000000000000000000000000000000000000'

interface ManageAssetsDialogProps {
  open: boolean
  onClose: () => void
}

interface ManageableToken {
  address: string
  symbol: string
  name: string
  logoUrl?: string
  network: string
  isVisible: boolean
}

function getManageableTokens(): ManageableToken[] {
  const tokens: ManageableToken[] = []

  getAssetNetworkIds().forEach((networkId) => {
    const network = getNetwork(networkId)
    const popularTokens = getPopularTokens(networkId).filter((t) => !t.isNative)
    const customTokens = storageService
      .getCustomTokens()
      .filter((t) => t.network === networkId)

    tokens.push({
      address: NATIVE_ADDRESS,
      symbol: network?.nativeCurrency?.symbol || 'ETH',
      name: network?.nativeCurrency?.name || network?.name || '',
      logoUrl: network?.nativeCurrencyLogoUrl,
      network: networkId,
      isVisible: storageService.isTokenVisible(NATIVE_ADDRESS, networkId)
    })

    ;[...popularTokens, ...customTokens].forEach((token) => {
      tokens.push({
        address: token.address,
        symbol: token.symbol,
        name: token.name,
        logoUrl: token.logoUrl,
        network: networkId,
        isVisible: storageService.isTokenVisible(token.address, networkId)
      })
    })
  })

  return tokens
}

const ManageAssetsDialog = ({ open, onClose }: ManageAssetsDialogProps) => {
  const { t } = useTranslation()
  const { refreshBalance, refreshAllNetworksBalance } = useBalance()
  const storeTokens = useWalletStore((state) => state.tokens)
  const storeAllNetworksTokens = useWalletStore((state) => state.allNetworksTokens)
  const setStoreTokens = useWalletStore((state) => state.setTokens)
  const setStoreAllNetworksTokens = useWalletStore((state) => state.setAllNetworksTokens)
  const [searchTerm, setSearchTerm] = useState('')
  const [tokens, setTokens] = useState<ManageableToken[]>([])
  // network:address ocultados esta sesión — se quitan sin volver a pedir balances.
  const hiddenNowRef = useRef<Set<string>>(new Set())
  const shownNewRef = useRef(false)

  useEffect(() => {
    if (open) {
      setTokens(getManageableTokens())
      setSearchTerm('')
      hiddenNowRef.current = new Set()
      shownNewRef.current = false
    }
  }, [open])

  const groupedTokens = useMemo(() => {
    const filtered = tokens.filter((token) => {
      const term = searchTerm.toLowerCase()
      return (
        token.symbol.toLowerCase().includes(term) ||
        token.name.toLowerCase().includes(term)
      )
    })

    const groups = new Map<string, ManageableToken[]>()
    filtered.forEach((token) => {
      const group = groups.get(token.network) || []
      group.push(token)
      groups.set(token.network, group)
    })

    return getAssetNetworkIds()
      .map((networkId) => ({
        networkId,
        network: getNetwork(networkId),
        tokens: groups.get(networkId) || []
      }))
      .filter((group) => group.tokens.length > 0)
  }, [tokens, searchTerm])

  const handleToggle = (token: ManageableToken, isVisible: boolean) => {
    storageService.setTokenVisibility(token.address, token.network, isVisible)
    const key = `${token.network}:${token.address.toLowerCase()}`
    if (isVisible) {
      hiddenNowRef.current.delete(key)
      shownNewRef.current = true
    } else {
      hiddenNowRef.current.add(key)
    }
    setTokens((prev) =>
      prev.map((t) =>
        t.address === token.address && t.network === token.network
          ? { ...t, isVisible }
          : t
      )
    )
  }

  const handleClose = () => {
    if (shownNewRef.current) {
      // Se mostró algo sin balance cacheado — hace falta traerlo de verdad.
      refreshBalance(false)
      refreshAllNetworksBalance()
    } else if (hiddenNowRef.current.size > 0) {
      // Solo se ocultó — se puede quitar de lo ya cargado sin pedirle nada a la red.
      const stillVisible = (t: { address: string; network?: string }) =>
        !hiddenNowRef.current.has(`${t.network}:${t.address.toLowerCase()}`)
      setStoreTokens(storeTokens.filter(stillVisible))
      setStoreAllNetworksTokens(storeAllNetworksTokens.filter(stillVisible))
    }
    onClose()
  }

  return (
    <div
      className="fixed inset-0 bg-opacity-50 bg-black/50 backdrop-blur-sm h-full w-full z-50"
      style={{ display: open ? 'block' : 'none' }}
    >
      <div className="relative h-dvh flex items-center justify-center p-4">
        <Card className="shadow-xl bg-app dark:bg-card w-full max-w-md justify-center relative">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-text-secondary z-10"
            aria-label="Close dialog"
            type="button"
          >
            <X className="w-6 h-6" />
          </button>
          {/* Radix's Card sets `display: block` in unlayered CSS that beats Tailwind's
              `flex` utility, so the flex/scroll layout is built on this plain div instead
              of on Card itself — Card just supplies the background/shadow/radius. */}
          <div className="flex flex-col max-h-[calc(100dvh-4rem)] overflow-hidden">
            <div className="flex flex-col gap-5 px-2 pt-4 pb-2 lg:px-4 shrink-0">
              <div className="flex flex-col gap-1 pr-8">
                <h2 className="text-text-main text-2xl font-semibold">
                  {t('dialogs.manage_assets_title')}
                </h2>
                <p className="text-text-secondary text-sm">
                  {t('dialogs.manage_assets_desc')}
                </p>
              </div>

              <SearchBar
                placeholder={t('dialogs.search_assets_placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-4 px-2 py-4 lg:p-4 overflow-y-auto min-h-0 scrollbar-thin">
              {groupedTokens.length === 0 ? (
                <div className="p-6 text-center text-text-secondary text-sm">
                  {t('dialogs.no_assets_found')}
                </div>
              ) : (
                groupedTokens.map(
                  ({ networkId, network, tokens: groupTokens }) => (
                    <div key={networkId} className="flex flex-col gap-2">
                      <p className="text-xs text-text-secondary font-semibold uppercase tracking-[2.4px] px-1">
                        {network?.name}
                      </p>
                      <div className="bg-card border border-border-base rounded-xl divide-y divide-border-base overflow-hidden">
                        {groupTokens.map((token) => (
                          <div
                            key={token.network + token.address}
                            className="w-full flex items-center gap-3 px-4 py-3.5"
                          >
                            <div className="w-7 h-7 rounded-full overflow-hidden bg-primary/20 flex items-center justify-center shrink-0">
                              {token.logoUrl ? (
                                <img
                                  src={token.logoUrl}
                                  alt={token.symbol}
                                  className="w-full h-full object-cover"
                                />
                              ) : network?.logoUrl ? (
                                <img
                                  src={network.logoUrl}
                                  alt={token.symbol}
                                  className="w-full h-full object-cover grayscale"
                                />
                              ) : (
                                <Globe className="w-4 h-4 text-primary" />
                              )}
                            </div>
                            <div className="flex flex-col flex-1 min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span className="text-sm font-medium text-text-main truncate">
                                  {token.symbol}
                                </span>
                                <span className="text-[10px] text-text-secondary bg-card-hover rounded px-1.5 py-0.5 shrink-0">
                                  {network?.name}
                                </span>
                              </div>
                              <span className="text-xs text-text-secondary truncate">
                                {token.name}
                              </span>
                            </div>
                            <Switch
                              checked={token.isVisible}
                              onCheckedChange={(checked) =>
                                handleToggle(token, checked)
                              }
                              color="amber"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )
                )
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  )
}

export default ManageAssetsDialog
